import { useState, useEffect } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { ArrowLeftIcon, LogOutIcon, SlidersIcon } from '@nimbus-ds/icons'
import { Text } from '@nimbus-ds/components'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/api/auth'
import { storeService } from '@/api/store'
import { MerchantChromeProvider, useMerchantChrome } from './MerchantChrome'
import { MerchantStoreSwitcher } from './MerchantStoreSwitcher'
import { storeDisplayName as displayName } from '@/lib/storeName'

/**
 * The REAL store name for the current membership. The JWT's `store_name` is the
 * USER's Google display name (or 'ECL Desk') — never the store — so the header
 * resolves it from the store API once and caches per store per session.
 */
function useStoreName(): string | null {
  const storeId = authService.getCurrentStoreId()
  const cacheKey = storeId ? `tnx_store_name_${storeId}` : null
  const [name, setName] = useState<string | null>(() =>
    cacheKey ? sessionStorage.getItem(cacheKey) : null
  )

  useEffect(() => {
    if (!storeId || !cacheKey || name) return
    let cancelled = false
    storeService
      .getStore(storeId)
      .then((info: any) => {
        const resolved = displayName(info?.name)
        if (resolved && !cancelled) {
          try { sessionStorage.setItem(cacheKey, resolved) } catch { /* quota */ }
          setName(resolved)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [storeId, cacheKey, name])

  return name
}

/**
 * Lean, mobile-first shell for the merchant "standup" view. Deliberately NOT the
 * consultant Layout (no sidebar, no dense nav) — a merchant checks this on their
 * phone between serving customers. One header, one content area.
 *
 * Handoff 0.2: the top bar carries each screen's identity (wizard step, test
 * dates); pages publish it via MerchantChrome. Content caps at 1200px with a
 * single 768px breakpoint — 400px mobile / 1200px desktop are the references.
 */
function MerchantHeader() {
  const { user, logout } = useAuth()
  // Consultants/admins reach this view to preview what a merchant sees — give
  // them a way back to the console. Actual merchants don't see this.
  const isPreview = !authService.isMerchant()
  const chrome = useMerchantChrome()
  const storeName = useStoreName() || 'Tu tienda'
  const email = (user as any)?.email

  const title = chrome?.title ?? storeName
  const defaultSubtitle = isPreview
    ? `Vista comerciante (previsualización)${email ? ` · ${email}` : ''}`
    : 'Resumen diario'
  const subtitle = chrome ? chrome.subtitle : defaultSubtitle
  // A page-owned bar keeps its own back target; the shell's own arrow is the
  // consultant's way out of the preview.
  const backTo = chrome ? chrome.backTo : isPreview ? '/dashboard' : undefined
  const backLabel = chrome ? chrome.backLabel || 'Volver' : 'Volver al panel'

  return (
    <header className="sticky top-0 z-10 border-b border-[#e7e7e7] bg-white/[.92] backdrop-blur">
      <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-3 px-4 py-3 md:px-8 md:py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          {backTo && (
            <Link to={backTo} className="text-muted-foreground hover:text-foreground" aria-label={backLabel}>
              <ArrowLeftIcon size={20} />
            </Link>
          )}
          <div className="min-w-0">
            <div className="truncate">
              <Text as="span" fontWeight="bold" color="neutral-textHigh">{title}</Text>
            </div>
            {subtitle && (
              <div className="truncate">
                <Text as="span" fontSize="caption">
                  {subtitle}
                  {chrome?.showStoreOnDesktop && (
                    <span className="hidden md:inline"> · {storeName}</span>
                  )}
                </Text>
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Renders only for multi-store users; single-store merchants see the
              header exactly as before. */}
          <MerchantStoreSwitcher />
          {/* Preferencias (posture) — the one switch a merchant declares; no onboarding form. */}
          {!chrome && (
            <Link to="/merchant/preferencias" className="text-muted-foreground hover:text-foreground" aria-label="Preferencias">
              <SlidersIcon size={20} />
            </Link>
          )}
          <button onClick={logout} className="text-muted-foreground hover:text-foreground" aria-label="Salir">
            <LogOutIcon size={20} />
          </button>
        </div>
      </div>
    </header>
  )
}

export function MerchantLayout() {
  return (
    <MerchantChromeProvider>
      <div className="min-h-screen bg-[var(--nimbus-colors-neutral-surface)]">
        <MerchantHeader />
        <main className="mx-auto max-w-[1200px] px-4 py-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </MerchantChromeProvider>
  )
}
